import type { TFunction } from "i18next"

import { AxisY, ChartMultiple, ChartMultipleItem, ChartRow } from "@/app/report/common/constructor"
import { ChartStyle, createChart } from "@/app/report/common/chart/multiple"
import {
  MAXIMUM_CHART_LEN_BY_BAR_CHART,
  countBarChartAxes,
} from "@/app/report/common/export/constants"
import { VALUE_ORNAMENT_TYPE, VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import type { GraphSettingsYAxis } from "@/app/report/common/type"

const DATA_LABEL_TO_POSITION: Record<string, VALUE_POSITION_TYPE> = {
  hideValues: VALUE_POSITION_TYPE.HIDE_VALUES,
  // 그래프 가운데(막대 안) — 가로 엔진은 IN_BAR(막대 안 위쪽), 세로 렌더러는 막대 안 가운데 (#185)
  insidePlots: VALUE_POSITION_TYPE.IN_BAR,
  abovePlots: VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION,
  separateTable: VALUE_POSITION_TYPE.ABOVE,
}

/** 리포트 템플릿의 dataLabel 문자열 → 엔진의 값 표시 위치 (리포트 화면 MultipleChart 와 같은 대응) */
export const dataLabelToValuePosition = (dataLabel?: string): VALUE_POSITION_TYPE =>
  (dataLabel && DATA_LABEL_TO_POSITION[dataLabel]) || VALUE_POSITION_TYPE.HIDE_VALUES

export type EngineChartGroup = { name: string; nickname: string | null; rows: ChartRow[] }

/**
 * 리포트 공용 차트 엔진(createChart)으로 차트 묶음을 컨테이너에 그린다 — PDF 빌더의 Session Details 등이 쓴다.
 * 리포트 PDF(pdf/IntervalChart)와 같은 출력용 스타일(글자 8px, 좁은 축 여백)이고, 한 차트에 항목이 많으면
 * 리포트 PDF 와 같은 상한(MAXIMUM_CHART_LEN_BY_BAR_CHART)으로 잘라 세로로 쌓는다.
 */
/**
 * createChart 를 폭에 맞춰 두 번까지 돌린다. 엔진은 축 공간을 축 수(axisY.length)만큼 잡아 두는데, 누적 막대(Stacked Bar)처럼
 * 축이 합쳐져 숨겨지면 그 공간이 남아 오른쪽이 비었다 (2026-09-28 보고). 실제로 차지한 폭(왼쪽 축 + 차트 + 오른쪽 축)이
 * 목표 폭보다 작으면 그 차이만큼 스타일 폭을 키워 다시 그린다 — 남는 폭은 엔진이 항목마다 고르게 나눠 준다.
 */
/** 엔진이 자리만 잡고 아무것도 그리지 않은 축 svg (누적 막대 2개만 있을 때의 오른쪽 축) */
export const hasDrawnContent = (svg: SVGSVGElement | null | undefined) =>
  !!svg && svg.childElementCount > 0

export function createChartFilled({
  targetWidth,
  makeStyle,
  ...rest
}: Omit<Parameters<typeof createChart>[0], "style"> & {
  targetWidth: number
  makeStyle: (width: number) => ChartStyle
}) {
  let width = targetWidth
  let result = createChart({ ...rest, style: makeStyle(width) })
  for (let pass = 0; pass < 2 && result; pass++) {
    // 그려진 축만 폭으로 친다 — 누적 막대 2개뿐이면 엔진은 오른쪽 축 자리만 잡고 비워 둔다 (2026-09-28 보고)
    const occupied =
      (result.svgYAxisLeft?.viewBox.baseVal.width ?? 0) +
      (hasDrawnContent(result.svgYAxisRight) ? result.svgYAxisRight!.viewBox.baseVal.width : 0) +
      result.svgCharts.reduce((sum, svg) => sum + (svg?.viewBox.baseVal.width ?? 0), 0)
    const slack = targetWidth - occupied
    if (slack <= 2) {
      break
    }
    width += slack
    result = createChart({ ...rest, style: makeStyle(width) })
  }

  return result
}

// ChartStyle 기본 xAxisLabelHeight (flagLongNameOnXAxis=false, multiple.ts:226)
const PREVIEW_X_AXIS_LABEL_PX = 40

/** 범례 블록(.BuilderD3Chart__legend)의 레이아웃 높이 — 글자 + 위아래 여백(builderCharts.scss 24/16px). 없으면 0 */
export function measureLegendBlockPx(root: HTMLElement | null | undefined): number {
  const legend = root?.querySelector<HTMLElement>(".BuilderD3Chart__legend")
  if (!legend) {
    return 0
  }
  const style = getComputedStyle(legend)

  return (
    legend.offsetHeight + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0)
  )
}

/** 범례를 아직 못 잰 순간의 추정값 — 한 줄 글자 15 + 여백 40 */
/** 값 표시 방식이 막대 영역 위에서 차지하는 높이 — 줄 높이에 더해 막대 크기를 지킨다 (2026-10-01 요청).
 *  모아서 보기(ABOVE)는 시리즈 수만큼 표 줄, 그래프 위(IN_CHART_EACH_POSITION)는 엔진이 위에 비우는 값 한 줄(8px×1.3+8px×0.8) */
export function dataLabelExtraPx(dataLabel: string | undefined, seriesCount: number): number {
  if (dataLabel === "separateTable") {
    return seriesCount * 16 + 12
  }
  if (dataLabel === "abovePlots" || dataLabel === "insidePlots") {
    return 18
  }

  return 0
}

export const LEGEND_FALLBACK_PX = 55

/**
 * 한 차트(줄)에 담을 항목 상한 — 막대 축 수(0·1·2·3)별. 리포트 PDF(MAXIMUM_CHART_LEN_BY_BAR_CHART = 14·14·12·10)보다
 * 하나씩 더 담는다 — 빌더는 항목 사이 여백을 줄여 세로 막대 기준 15개까지 한 줄에 그린다 (2026-09-30 요청)
 */
const BUILDER_MAX_PER_LINE = MAXIMUM_CHART_LEN_BY_BAR_CHART.map((n) => n + 1)

/** 한 차트(줄)에 담을 항목 상한. 넘치면 같은 이름으로 다음 줄에 이어 그린다 */
function splitEngineLines(groups: EngineChartGroup[], yAxisSettings: GraphSettingsYAxis[]) {
  const maxPerChart = BUILDER_MAX_PER_LINE[countBarChartAxes(yAxisSettings)] ?? 15
  const lines: EngineChartGroup[][] = []
  groups.forEach((group) => {
    const rows = group.rows
    if (rows.length <= maxPerChart) {
      lines.push([group])

      return
    }
    for (let i = 0; i < rows.length; i += maxPerChart) {
      lines.push([{ ...group, rows: rows.slice(i, i + maxPerChart) }])
    }
  })

  return { maxPerChart, lines }
}

/** 그리지 않고 줄 수만 센다 — 페이지 경계 계산과 칸 높이를 그리기 전에 정하기 위해 */
export function countEngineLines(
  groups: EngineChartGroup[],
  yAxisSettings: GraphSettingsYAxis[],
): number {
  return groups.length ? splitEngineLines(groups, yAxisSettings).lines.length : 0
}

export function drawEngineChart({
  container,
  groups,
  axisY,
  yAxisSettings,
  width,
  height,
  valuePositionType,
  decimalPoint,
  translateKey,
  t,
  lineHeightPx,
  padTops,
}: {
  container: HTMLElement
  groups: EngineChartGroup[]
  axisY: AxisY[]
  yAxisSettings: GraphSettingsYAxis[]
  width: number
  height: number
  valuePositionType: VALUE_POSITION_TYPE
  decimalPoint: number
  translateKey: (key: string) => string | undefined
  t: TFunction
  /** 줄 높이를 고정할 때(페이지 인식 배치). 없으면 height 를 줄 수로 나눈다 */
  lineHeightPx?: number
  /** 줄마다 앞에 둘 빈 공간(px) — 페이지 경계에 걸리는 줄을 다음 페이지로 밀 때 */
  padTops?: number[]
}): number {
  container.innerHTML = ""
  if (width < 50 || (!lineHeightPx && height < 50) || !groups.length) {
    return 0
  }

  const { maxPerChart, lines } = splitEngineLines(groups, yAxisSettings)

  // isPreview 스타일은 SVG 높이를 style.height + xAxisLabelHeight(40, multiple.ts:785) 로 잡는다 — 그만큼 빼서 넘겨야
  // 줄 높이가 나눠 준 높이 안에 들어간다. 안 빼면 줄마다 40px 씩 넘쳐 마지막 줄이 잘렸다 (2026-09-30 보고)
  const perLineHeight =
    (lineHeightPx ?? Math.max(120, Math.floor(height / lines.length))) - PREVIEW_X_AXIS_LABEL_PX
  const highest = groups
    .flatMap((group) => group.rows)
    .reduce<number[]>((acc, row) => {
      row.data.forEach((value, index) => {
        acc[index] = Math.max(acc[index] ?? 0, value ?? 0)
      })

      return acc
    }, [])

  lines.forEach((line, lineIndex) => {
    const charts = line.map(
      (group) => new ChartMultipleItem(group.name, group.nickname, group.rows, highest),
    )
    const chartMultiple = new ChartMultiple(axisY, charts)
    const makeStyle = (styleWidth: number) => {
      const style = new ChartStyle({ width: styleWidth, height: perLineHeight, magnification: 1 })
      style.fullWidth = false
      style.alignLeft = false
      style.referenceXAxisItemCount = Math.min(
        maxPerChart,
        Math.max(...groups.map((group) => group.rows.length)),
      )
      style.flagShowTickStroke = false
      // 15개가 한 줄에 들어가게 막대·항목 사이 여백을 리포트 PDF 보다 조금 줄인다
      style.arrayWidthOfBar = [15, 15, 12]
      style.space.marginBetweenItemsRatio = 0.5
      style.space.yAxisLabelWidth = 16
      style.space.betweenRightYAxis = 6
      style.minYAxisTickValueWidth = 14
      style.valuePositionType = valuePositionType
      // 값 글자는 시리즈 색 + 흰 테두리 (2026-10-01 요청)
      style.valueOrnamentType = VALUE_ORNAMENT_TYPE.USE_CHART_COLOR
      style.isPreview = true
      style.fontSize = 8
      style.fontSizeValue = 8
      style.fontSizeNoData = 8
      if (chartMultiple.charts.length === 1 && !line[0].nickname) {
        style.space.xAxisChartNameWidth = 0
      }

      return style
    }

    const result = createChartFilled({
      targetWidth: width,
      makeStyle,
      chartMultiple,
      translateKey,
      mouseover: () => undefined,
      mousemove: () => undefined,
      mouseout: () => undefined,
      decimalPointForExportFile: decimalPoint,
      t,
    })
    if (!result) {
      return
    }

    const row = document.createElement("div")
    row.className = "BuilderD3Chart__chart-row"
    row.style.paddingTop = `${padTops?.[lineIndex] ?? 0}px`
    const table = document.createElement("table")
    table.style.width = "100%"
    const tbody = document.createElement("tbody")
    const tr = document.createElement("tr")

    const appendSvg = (td: HTMLTableCellElement, svg: SVGSVGElement | null | undefined) => {
      if (!hasDrawnContent(svg) || !svg) {
        return
      }
      const div = document.createElement("div")
      div.style.width = `${svg.viewBox.baseVal.width}px`
      div.style.height = `${svg.viewBox.baseVal.height}px`
      div.appendChild(svg)
      td.appendChild(div)
    }

    const tdLeft = document.createElement("td")
    tdLeft.className = "BuilderD3Chart__y-axis-left"
    appendSvg(tdLeft, result.svgYAxisLeft)
    tr.appendChild(tdLeft)

    const tdChart = document.createElement("td")
    const chartContainer = document.createElement("div")
    chartContainer.className = "BuilderD3Chart__chart-elements"
    result.svgCharts.forEach((svgChart) => {
      if (svgChart) {
        const div = document.createElement("div")
        div.style.width = `${svgChart.viewBox.baseVal.width}px`
        div.style.height = `${svgChart.viewBox.baseVal.height}px`
        div.appendChild(svgChart)
        chartContainer.appendChild(div)
      }
    })
    tdChart.appendChild(chartContainer)
    tr.appendChild(tdChart)

    const tdRight = document.createElement("td")
    tdRight.className = "BuilderD3Chart__y-axis-right"
    appendSvg(tdRight, result.svgYAxisRight)
    tr.appendChild(tdRight)

    tbody.appendChild(tr)
    table.appendChild(tbody)
    row.appendChild(table)
    container.appendChild(row)
  })

  return lines.length
}
